import { Component, OnInit, AfterViewInit, OnDestroy } from '@angular/core';
import { Subject } from 'rxjs';
import { BsModalRef } from 'ngx-bootstrap';
import { PlaidLinkHandler } from 'ngx-plaid-link/lib/ngx-plaid-link-handler';
import { PlaidConfig } from '@shared/services/plaid/interfaces';
import { PLAID, environment } from '@env/environment';
import { NgxPlaidLinkService } from 'ngx-plaid-link';
import { PAYMENT_METHOD } from '@shared/constants/property-details';
import {
  PlaidService,
  CurrentUserService,
  WalletSharedService
} from '@shared/services';
import { Router } from '@angular/router';
import { MAX_AMOUNT } from '@shared/constants/plaid';

@Component({
  selector: 'app-add-funds',
  templateUrl: './add-funds.component.html',
  styleUrls: ['./add-funds.component.scss']
})
export class AddFundsComponent implements OnInit, AfterViewInit, OnDestroy {
  public onClose: Subject<string>;
  isAddFund: boolean = false;
  username;
  plaidData;
  isReady: boolean = false;
  institutionName: string = '';
  accounts: Array<any> = [];
  isPlaidReady: boolean = false;
  openingPlaid: boolean = false;
  isAccountExist: boolean = false;
  customerNumber: string;

  paymentMethod: number = 1;

  // form
  selected;
  selectedAccount;
  invalidAccount: boolean = false;
  isSubmitted: boolean = false;
  amount: number = 0;
  isMethodOpen;
  methods;

  // Plaid
  private plaidLinkHandler: PlaidLinkHandler;

  private config: PlaidConfig = {
    clientName: 'Partbnb',
    apiVersion: 'v2',
    // env: 'sandbox',
    env: 'production',
    institution: null,
    token: null,
    webhook: `${environment.apiUrl}/${PLAID.WEBHOOK}`,
    // product: ['transactions', 'auth', 'income', 'identity', 'assets'],
    product: ['transactions', 'auth'],
    key: PLAID.PUBLIC_KEY
  };
  constructor(
    private bsModalRef: BsModalRef,
    private plaidLinkService: NgxPlaidLinkService,
    private router: Router,
    private _plaid: PlaidService,
    private _currentUser: CurrentUserService,
    private _walletShared: WalletSharedService
  ) {}

  ngOnInit() {
    this.onClose = new Subject();
    const { userName, customerNumber } = this._currentUser.getUserInfo();
    this.username = userName;
    this.customerNumber = customerNumber || '';
    this.checkExistingAccount();
    this.getAccounts();
    this._walletShared.updatePlaid.subscribe(plaid => {
      this.isAddFund = plaid === 'add-fund';
    });
    this._walletShared.togglePlaid('add-fund');
    this.isMethodOpen = false;
    this.methods = [
      { id: PAYMENT_METHOD.ACH_TRANSFER, name: 'ACH Transfer' },
      { id: PAYMENT_METHOD.WIRE_TRANSFER, name: 'Wire Details' }
    ];
  }

  ngAfterViewInit() {
    this.plaidLinkService
      .createPlaid(
        Object.assign({}, this.config, {
          onSuccess: (token, metadata) => this.onSuccess(token, metadata),
          onExit: () => {
            this.openingPlaid = false;
          },
          onEvent: () => {}
        })
      )
      .then((handler: PlaidLinkHandler) => {
        this.plaidLinkHandler = handler;
        this.isPlaidReady = true;
      });
  }

  ngOnDestroy() {
    this.isPlaidReady = false;
  }

  onSuccess(token, metadata) {
    if (!this.isAddFund) {
      return;
    }
    this.plaidData = {
      public_token: token,
      data: metadata
    };
    this.institutionName = this.plaidData.data.institution.name;
    this.accounts = this.plaidData.data.accounts;
    this.selected =
      (this.plaidData.data.accounts &&
        this.plaidData.data.accounts.length &&
        this.plaidData.data.accounts[0].id) ||
      '';
    this.selectedAccount =
      (this.plaidData.data.accounts &&
        this.plaidData.data.accounts.length &&
        this.plaidData.data.accounts[0]) ||
      {};
    this.getAccessToken();
    this.isReady = true;
  }

  checkExistingAccount() {
    this._plaid.checkAccount(this.username).subscribe(
      res => {
        if (res) {
          this.isAccountExist = res.exists || false;
          if (this.isAccountExist) {
            this.institutionName = res.accountBankName;
          }
        } else {
          this.isAccountExist = false;
        }
      },
      () => {
        this.isAccountExist = false;
      }
    );
  }

  getAccounts() {
    this._plaid.getAccounts(this.username).subscribe(res => {
      if (!res || !res.accounts) {
        return;
      }
      this.accounts = res.accounts.map(i => {
        i.id = i.account_id;
        return i;
      });
      this.selected = this.accounts[0].account_id;
      this.selectedAccount = this.accounts[0];
      this.isReady = true;
    });
  }

  selectAccount() {
    this.invalidAccount = false;
    this.selectedAccount = this.accounts.find(i => i.id === this.selected);
  }

  getAccessToken() {
    const postData = {
      public_token: this.plaidData.public_token,
      bank_name: this.institutionName
    };
    this._plaid.getAccessToken(postData, this.username).subscribe();
  }

  changeMethod(method) {
    this.paymentMethod = method;
  }

  isACHTransfer() {
    return this.paymentMethod === PAYMENT_METHOD.ACH_TRANSFER;
  }

  isWireTransfer() {
    return this.paymentMethod === PAYMENT_METHOD.WIRE_TRANSFER;
  }

  plaidOpen() {
    if (this.openingPlaid || !this.isPlaidReady) {
      return;
    }
    this.openingPlaid = true;
    this.plaidLinkHandler.open();
  }

  getStripeToken() {
    if (this.isSubmitted) {
      return;
    }
    this.invalidAccount = false;
    this.isSubmitted = true;
    const postData = {
      AccountId: this.selectedAccount.id,
      AccountMask: this.selectedAccount.mask,
      AccountBankName: this.institutionName
    };
    this._plaid.createStripeToken(postData, this.username).subscribe(
      res => {
        if (res && res.stripe_bank_account_token) {
          const bankData = {
            StripeBankToken: res.stripe_bank_account_token,
            Amount: this.amountFilter(`${this.amount}`)
          };
          this.charge(bankData);
        } else {
          this.invalidAccount = true;
          this.isSubmitted = false;
        }
      },
      () => {
        this.invalidAccount = true;
        this.isSubmitted = false;
      }
    );
  }

  charge(data: any) {
    this._plaid.stripeChargeForOrder(data, this.username).subscribe(
      () => {
        this.router.navigate(['/dashboard/wallet']);
        this.close('done');
        this.isSubmitted = false;
      },
      () => {
        this.invalidAccount = true;
        this.isSubmitted = false;
      }
    );
  }

  checkAmount() {
    if (this.amount === 0) {
      this.amount = null;
    } else if (!this.amount) {
      this.amount = 0;
    }
  }

  amountFilter(value) {
    const period = '.';
    let newValue = '';
    const parts = value.split(period);
    if (parts[1] === undefined) {
      newValue = value.replace(/\D/g, '') + '.00';
    } else {
      const lastPart = parts.slice(-1)[0].replace(/\D/g, '');
      const tempPart =
        lastPart.length < 2 ? `${lastPart}0` : lastPart.substring(0, 2);
      newValue = parts.slice(0, -1).join('') + period + [tempPart];
    }
    return newValue.replace('.', '');
  }

  isAmountisExeed() {
    return this.amount > MAX_AMOUNT;
  }

  close(response: string = '') {
    this.onClose.next(response);
    this.bsModalRef.hide();
  }
}
